import { makeAutoObservable } from 'mobx'
import { type IHubspotSettingsDict, type Integration } from 'entities/Integrations'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { IntegrationCustomSettingsStore } from 'pages/settings/pages/integrations/pages/integrationSettings/store/IntegrationCustomSettingsStore'
import { IntegrationCollapseSettingsStore } from 'pages/settings/pages/integrations/pages/integrationSettings/store/IntegrationCollapseSettingsStore'
import { IntegrationHubspotSettingsChannelsStore } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationHubspotSettings/store/IntegrationHubspotSettingsChannelsStore'
import { IntegrationHubspotSettingsTimelineStore } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationHubspotSettings/store/IntegrationHubspotSettingsTimelineStore'
import { IntegrationSettingsUsersStore } from 'pages/settings/pages/integrations/pages/integrationSettings/store/IntegrationSettingsUsersStore'
import { IntegrationWebhookStore } from 'pages/settings/pages/integrations/store/IntegrationWebhookStore'
import { IntegrationSettingsPropertyStore } from 'pages/settings/pages/integrations/pages/integrationSettings/store/IntegrationSettingsPropertyStore'
import { IntegrationSettingsSyncStore } from 'pages/settings/pages/integrations/pages/integrationSettings/store/IntegrationSettingsSyncStore'
import { HUBSPOT_SYNC_CHECKBOX_KEYS } from 'pages/settings/pages/integrations/pages/integrationSettings/lib'
import { type IIntegrationStore } from 'pages/settings/pages/integrations/pages/integrationSettings/hooks/useIntegrationStoreMemo'
import { IntegrationHubspotSettingsListsStore } from './IntegrationHubspotSettingsListsStore'
import { IntegrationHubspotSettingsTicketOwnerStore } from './IntegrationHubspotSettingsTicketOwnerStore'

export class IntegrationHubspotSettingsStore implements IIntegrationStore {
  private _collapseStore
  private _syncStore
  private _webhookStore
  private _userStore
  private _channelsStore
  private _timelineStore
  private _propertyStore
  private _customSettingsStore
  private _listsStore
  private _ticketOwnerStore

  constructor(integration: Integration, private _entities: IEntitiesStore) {
    this._collapseStore = new IntegrationCollapseSettingsStore()
    this._webhookStore = new IntegrationWebhookStore(this._entities)
    this._customSettingsStore = new IntegrationCustomSettingsStore<IHubspotSettingsDict>(
      integration
    )
    this._channelsStore = new IntegrationHubspotSettingsChannelsStore(integration)
    this._userStore = new IntegrationSettingsUsersStore(integration, this._entities)
    this._ticketOwnerStore = new IntegrationHubspotSettingsTicketOwnerStore(this._entities)

    const hubspotExpandedCheckboxHandler = (type: string, value: boolean): string[] => {
      if (type === 'update_contacts' && value) {
        return ['update_owner', type]
      }
      return [type]
    }

    this._syncStore = new IntegrationSettingsSyncStore<IHubspotSettingsDict>(
      integration,
      this._customSettingsStore,
      HUBSPOT_SYNC_CHECKBOX_KEYS,
      hubspotExpandedCheckboxHandler
    )

    this._propertyStore = new IntegrationSettingsPropertyStore(
      integration,
      this._customSettingsStore,
      this._entities
    )
    this._timelineStore = new IntegrationHubspotSettingsTimelineStore(
      this._customSettingsStore,
      this._entities
    )
    this._listsStore = new IntegrationHubspotSettingsListsStore()

    makeAutoObservable(this)
  }

  init = (integration: Integration) => {
    this._customSettingsStore.init()
    this._webhookStore.initIntegrationWebhookState(integration)
    this._channelsStore.init()
    this._userStore.init()
    this._syncStore.init()
    this._propertyStore.init()
    this._timelineStore.init()
    this._listsStore.init()
  }

  dispose = () => {
    this._syncStore.dispose()
    this._propertyStore.dispose()
    this._channelsStore.dispose()
    this._userStore.dispose()
    this._webhookStore.dispose()
  }

  get syncStore() {
    return this._syncStore
  }

  get webhookStore() {
    return this._webhookStore
  }

  get userStore() {
    return this._userStore
  }

  get timelineStore() {
    return this._timelineStore
  }

  get propertyStore() {
    return this._propertyStore
  }

  get channelsStore() {
    return this._channelsStore
  }

  get listsStore() {
    return this._listsStore
  }

  get collapseStore() {
    return this._collapseStore
  }

  get ticketOwnerStore() {
    return this._ticketOwnerStore
  }
}
